import classNames from 'classnames';
import { get, isEmpty } from 'lodash';
import { useRouter } from 'next/router';
import { ChangeEvent, FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import Button from '~/components/Button';
import Container from '~/components/Container';
import Icon from '~/components/Icon';

import Star from '~/components/Star';
import Text from '~/components/Text';
import Colors from '~/constants/colors';
import { Identifiers, PATH } from '~/constants/enum';

import { useQuery } from '@apollo/client';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import TruckFastIcon from '~/assets/svgs/truck-fast.svg';
import CustomizationOptions from '~/components/CustomizationOptions';
import ProductDetailImage from '~/components/ProductDetailImage';
import ProductIndex from '~/components/ProductIndex/ProductIndex';
import TabReviews from '~/components/TabReviews';
import { useCart } from '~/data/cart';
import { QUERY_GET_CMS_BLOCKS } from '~/data/home';
import { QUERY_PRODUCT_INDEX, QUERY_PRODUCT_NOTE, QUERY_RELATED_TRACKING } from '~/data/product/gql';
import useClient from '~/hooks/useClient';
import { CartAtom } from '~/services/cart';
import { CountriesAtom } from '~/services/countries';
import { EstimateShippingAtom } from '~/services/estimate-shipping';
import { Product, ProductConfigurableOption, ProductVariant } from '~/services/product';
import storeConfigAtom from '~/services/store-config/store-config.atom';
import { trackingAddToCartClick, trackingBuyNowClick, trackingOnProductClick } from '~/services/tracking';
import ProductDetailMoreInfo from '../ProductDetailMoreInfo';
import StepShipping from '../ProductDetailMoreInfo/tabs/ProductShipping/StepShipping';
import ProductVariants from '../productVariants';
import styles from './ProductDetailTwo.module.scss';
import TooltipTwo from '~/components/TooltipTwo';

interface ProductDetailTwoProps {
    actions?: ReactNode[];
    config?: ProductConfigurableOption[];
    type?: 'simple' | 'personalized' | 'custom';
    renderActions?: (payload: { onAddToCart: () => void; onBuyNow: () => void }) => ReactNode;
    product: Partial<Product>;
    defaultVariant: ProductVariant;
}

const ProductDetailTwo: FC<ProductDetailTwoProps> = ({ actions, config, renderActions, product, defaultVariant }) => {
    // const themeConfig = getConfig('ProductDetail');

    const { data: dataCutOff, called } = useQuery(QUERY_GET_CMS_BLOCKS, {
        variables: {
            identifiers: [Identifiers.CutOff, Identifiers.CutOffAfter]
        }
    });

    const CountriesAtomState = useRecoilValue(CountriesAtom.countriesSelector);
    const { windowWidth } = useRecoilValue(storeConfigAtom.store);

    const [quantity, setQuantity] = useState(1);

    const [variant, setVariant] = useState<ProductVariant>(defaultVariant);
    const [validCustomOptions, setValidCustomOptions] = useState({});
    const [valueCustomization, setValueCustomization] = useState<{
        options: string[];
        optionById: { [key: string]: any };
    }>({
        options: [],
        optionById: {}
    });

    const [loadingAddToCartWithoutCartId, setLoadingAddToCartWithoutCartId] = useState(false);

    const router = useRouter();
    const { addToCart, loadingAddToCart } = useCart();

    const variantId = router.query.variant as string;

    const cart = useRecoilValue(CartAtom.cartSelector);
    const { progress } = useRecoilValue(EstimateShippingAtom.estimateShippingSelector);
    const {
        shippingMethodSelected,
        countrySelected,
        shippingMethodByCountry,
        estimateShipping: _estimateShippingRecoil
    } = useRecoilValue(CountriesAtom.countriesSelector);
    const { data: dataProductIndex } = useQuery(QUERY_PRODUCT_INDEX, {
        variables: {
            sku: product?.sku
        }
    });

    const { data } = useQuery(QUERY_RELATED_TRACKING, {
        variables: {
            sku: product?.sku
        },
        skip: !product?.sku
    });

    const currentAction = useRef<string>('');

    const checkValidateCustomOptions = (data: { options: string[]; optionById: any }) => {
        if (!data) return false;
        if (data.options.length === 0) return false;
        const tempOptionById = data.optionById;
        let valid: any = {};
        const isValid = data?.options
            ?.filter((i: any) => {
                return !!tempOptionById[i]?.required;
            })
            ?.every((item: any) => {
                return !!tempOptionById[item] && !!tempOptionById[item]?.value;
            });
        data?.options?.forEach((item: any) => {
            valid[item] =
                !!tempOptionById[item]?.required && !tempOptionById[item]?.value ? 'This field is required' : '';
            // if (element) {
            //     element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // }
        });

        setValidCustomOptions(valid);

        return !isValid;
    };

    const handleAddToCart = (name: 'add_to_cart' | 'buy_now' | 'more_payment') => {
        if (!cart.cartId) return;
        currentAction.current = name;
        if (!!valueCustomization && checkValidateCustomOptions(valueCustomization)) {
            const element = document.getElementsByName(Object.keys(validCustomOptions)[0])[0];
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }
        const productNote = valueCustomization?.options
            ?.filter((i: any) => {
                const item = valueCustomization?.optionById[i];
                return !!item?.required || !!item?.value;
            })
            ?.map((item) => {
                return {
                    name: valueCustomization.optionById[item].name,
                    type: valueCustomization.optionById[item].type,
                    value: valueCustomization.optionById[item].value + ''
                };
            });
        trackingAddToCartClick({ variant, product, qty: quantity, cartId: cart?.cartId });

        addToCart({
            variables: {
                sku: variant.product.sku,
                quantity: quantity,
                cartId: cart.cartId as any,
                parentSku: product?.sku || '',
                openCart: name === 'add_to_cart',
                productNote: productNote
            }
        });
        if (name === 'buy_now') {
            router.push('/checkout');
        }
    };

    const onBuyNow = useCallback(
        (name: 'add_to_cart' | 'buy_now' | 'more_payment') => {
            trackingBuyNowClick({ variant, product });
            handleAddToCart(name);
            router.push('/checkout');
        },
        [trackingBuyNowClick, variant, product, handleAddToCart]
    );

    useEffect(() => {
        if (!loadingAddToCart) {
            currentAction.current = '';
        }
    }, [loadingAddToCart]);

    const defaultProduct = useMemo(() => {
        return [];
    }, [cart?.items]);

    const handleBuyNow = useCallback(
        (name: any) => {
            if (!!cart.loading) return;

            trackingBuyNowClick({ variant, product });
            handleAddToCart(name);
        },
        [handleAddToCart, cart]
    );

    const renderActionButtons = () => {
        if (product.type === 'custom') {
            return (
                <Button
                    prefixIcon="magicpen"
                    loading={loadingAddToCart}
                    onClick={() => {
                        trackingOnProductClick(variant);
                        router.push(
                            {
                                pathname: PATH.ProductDetail,
                                query: { variant: variantId, slug: product.slug }
                            },
                            ''
                        );
                    }}
                >
                    Custom Now
                </Button>
            );
        }

        if (renderActions) {
            return renderActions?.({
                onAddToCart: () => handleAddToCart('add_to_cart'),
                onBuyNow: () => handleBuyNow('buy_now')
            });
        }

        return (
            <>
                <div className="w-full gap-1 lg:gap-2 flex flex-row">
                    <Button
                        className="flex-1 !text-lg"
                        type="outline"
                        variant="secondary"
                        loading={
                            currentAction.current === 'add_to_cart' &&
                            (loadingAddToCart || loadingAddToCartWithoutCartId)
                        }
                        onClick={() => handleAddToCart('add_to_cart')}
                        disabled={loadingAddToCart}
                    >
                        Add to cart
                    </Button>

                    <Button
                        className="flex-1 !text-lg"
                        loading={
                            currentAction.current === 'buy_now' && (loadingAddToCart || loadingAddToCartWithoutCartId)
                        }
                        onClick={() => handleBuyNow('buy_now')}
                        disabled={loadingAddToCart}
                    >
                        Buy Now
                    </Button>
                </div>
                {/* <div className="text-center mt-2 " onClick={() => handleBuyNow('more_payment')}>
                    <span
                        className={classNames('hover:cursor-pointer underline', {
                            'opacity-50 cursor-not-allowed': currentAction.current === 'more_payment'
                        })}
                    >
                        More payment options
                    </span>
                </div> */}
            </>
        );
    };

    const onChangeConfig = (variant: ProductVariant) => {
        if (variant) {
            setVariant(variant);
            router.push(
                {
                    pathname: PATH.ProductDetail,
                    query: {
                        ...router.query,
                        variant: variant.product.id,
                        slug: router.query.slug
                    }
                },
                undefined,
                { shallow: true }
            );
        }
    };

    const handleOnChangeCustomization = (value: any) => {
        setValueCustomization(value);
    };

    const quantityRef = useRef<any>(null);
    const handleInput = (e: ChangeEvent<HTMLDivElement>) => {
        const newQuantity = e.target.innerText.trim();
        // Validate if the new quantity is a valid number and within the range
        if (/^\d+$/.test(newQuantity)) {
            const number = parseInt(newQuantity, 10);
            if (number > Number.MAX_SAFE_INTEGER) {
                // e.target.innerText = "999"
                setQuantity(number - 1);
            } else if (number < 1) {
                // e.target.innerText = "1"
                setQuantity(1);
            } else {
                setQuantity(number);
            }
        } else {
            // If not a valid number, reset to the initial value
            // e.target.innerText = quantity+""
        }
    };

    const productIndex: {
        last_24h_view_count: number;
        last_24h_sale_count: number;
        last_24h_in_cart_count: number;
    } = useMemo(() => {
        return dataProductIndex?.productIndex;
    }, [dataProductIndex]);

    const _estimateShipping = useMemo(() => {
        if (!progress) return [];

        const _estimateDelivery = progress;
        return _estimateDelivery
            ?.map((es) => {
                const shipping = shippingMethodByCountry?.find((sh) => sh.carrier === es.carrier_code) || {};
                return {
                    ...es,
                    ...shipping
                };
            })
            .filter((es: any) => !!es?.title);
    }, [progress, shippingMethodByCountry]);

    const currentEstimate = useMemo(() => {
        if (!isEmpty(_estimateShipping) && !isEmpty(shippingMethodSelected)) {
            const defaultShipping =
                _estimateShipping?.filter((i) => i.carrier_code === 'freeshipping')?.[0] ??
                _estimateShipping?.[0] ??
                ({} as any);
            const shipping = _estimateShipping?.find((es) => es.carrier_code === shippingMethodSelected?.carrier);

            return shipping ?? defaultShipping;
        }

        return {} as any;
    }, [shippingMethodSelected?.carrier, _estimateShipping]);

    const isFreeShip = useMemo(() => {
        return CountriesAtomState?.shippingMethodByCountry?.find((i) => i.carrier === 'freeshipping');
    }, [CountriesAtomState?.shippingMethodByCountry]);

    const isClient = useClient();

    // if (isEmpty(product)) return null;
    // if (isEmpty(variant)) return null;

    const finalPrice = get(variant, 'product.price_range.minimum_price.final_price.value', 0);
    const regularPrice = get(variant, 'product.price_range.minimum_price.regular_price.value', 0);
    const discountPercent = get(variant, 'product.price_range.minimum_price.discount.percent_off', 0);
    const toPrevDateOrderCutOffAfter = 23 - currentEstimate?.originalData?.ships_delivered_to;
    const isBeforeChristmas = 23 >= new Date().getDate() + currentEstimate?.originalData?.ships_delivered_to;

    const textBeforeChristmas = get(dataCutOff, 'cmsBlocks.items', [])?.find(
        (i: any) => i.identifier === Identifiers.CutOff
    )?.content;
    const textAfterChristmas = get(dataCutOff, 'cmsBlocks.items', [])?.find(
        (i: any) => i.identifier === Identifiers.CutOffAfter
    )?.content;

    const currentDay = new Date().getDate();
    const month = new Date().getMonth();

    const isDisplay = currentDay < 25 && month === 11;
    return (
        <React.Fragment>
            <div className="lg:w-1/2 block lg:hidden">
                {/* lấy hình trong bộ variant trùng & là variant đầu tiên */}
                {/* Chỉ cần thay cái hình vị trí thứ 0 */}
                <ProductDetailImage
                    isBestSeller={!!productIndex?.last_24h_sale_count}
                    product={product}
                    variant={variant}
                />
            </div>
            <Container className="flex flex-col lg:flex-row lg:gap-4">
                <div className="lg:w-1/2 hidden lg:block">
                    {/* lấy hình trong bộ variant trùng & là variant đầu tiên */}
                    {/* Chỉ cần thay cái hình vị trí thứ 0 */}

                    <ProductDetailImage
                        isBestSeller={!!productIndex?.last_24h_sale_count}
                        product={product}
                        variant={variant}
                    />

                    {windowWidth >= 1024 && (
                        <TabReviews product_rating_summary={product.rating_summary} sku={product.sku} />
                    )}
                </div>
                <div className="flex flex-col flex-1 gap-3 px-2">
                    <div className="flex flex-col ">
                        {/* product index */}
                        <ProductIndex data={productIndex} />
                        {/* Price */}
                        <div className="flex items-center gap-1">
                            <ProductPrice
                                finalPrice={finalPrice}
                                regularPrice={regularPrice}
                                discountPercent={discountPercent}
                                loading={loadingAddToCart}
                            />

                            <div className={classNames('ml-2 gap-1', styles.tags)}>
                                {!!discountPercent && (
                                    <div className={classNames(styles.tag_item, styles.discount)}>
                                        <Text>{`${discountPercent}% off`}</Text>
                                    </div>
                                )}

                                {product.badges?.map((badge) => (
                                    <div key={badge} className={classNames(styles.tag_item, styles[badge])}>
                                        <Text>{badge}</Text>
                                    </div>
                                ))}

                                {isClient && !!isFreeShip && (
                                    <div className={classNames(styles.tag_item, styles.freeShip)}>
                                        <Text type="label-4">{isFreeShip?.title} </Text>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* name */}
                        <div className="flex items-center">
                            <h1 className="text-[1rem] lg:text-[1.2rem] !font-normal" style={{ lineHeight: '125%' }}>
                                {product?.name}
                            </h1>
                        </div>
                        {/* review */}
                        <div className="flex justify-between">
                            {!!product.review_count && (
                                <div
                                    onClick={() => {
                                        if (isClient) {
                                            const reviewTabsElement = document.getElementById('review-tabs');
                                            reviewTabsElement?.scrollIntoView({ behavior: 'smooth' });
                                        }
                                    }}
                                    title={product.rating_summary + ''}
                                    className="flex items-center gap-2 cursor-pointer"
                                >
                                    <Star value={product.rating_summary} size={16} />{' '}
                                    <span className="underline">{`(${product.review_count} reviews)`}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* mini info */}
                    <div className="flex flex-col ">
                        <div className={classNames('flex gap-2 ')}>
                            <Text type="label-3" className="flex flex-wrap gap-1 items-center">
                                <span className="flex flex-nowrap gap-1">
                                    <img
                                        src="/svgs/tick-double.svg"
                                        alt="tick-double"
                                        width={20}
                                        height={20}
                                        style={{
                                            color: Colors.PRIMARY
                                        }}
                                    />{' '}
                                    Order today to get by
                                </span>
                                {isClient && (
                                    <TooltipTwo
                                        id="step-shipping"
                                        title={
                                            <div className="flex flex-col  gap-2 ">
                                                <Text type="label-3" className="font-semibold">
                                                    {currentEstimate.title}
                                                </Text>
                                                <StepShipping steps={currentEstimate?.steps} />
                                            </div>
                                        }
                                    >
                                        {currentEstimate?.shipsDeliveredFrom?.format('MMM') ===
                                        currentEstimate?.shipsDeliveredTo?.format('MMM') ? (
                                            <Text type="label-3" weight={600} className="underline">
                                                {currentEstimate?.shipsDeliveredFrom?.format('MMM DD')}
                                                {'-'}
                                                {currentEstimate?.shipsDeliveredTo?.format('DD')}
                                            </Text>
                                        ) : (
                                            <Text type="label-3" weight={600} className="underline">
                                                {currentEstimate?.shipsDeliveredFrom?.format('MMM DD')}
                                                {'-'}
                                                {currentEstimate?.shipsDeliveredTo?.format('MMM DD')}
                                            </Text>
                                        )}
                                    </TooltipTwo>
                                )}
                            </Text>
                        </div>
                        <Text type="label-3" className="flex gap-1 items-center">
                            {' '}
                            <img
                                src="/svgs/tick-double.svg"
                                alt="tick-double"
                                width={20}
                                height={20}
                                style={{
                                    color: Colors.PRIMARY
                                }}
                            />{' '}
                            <a href="/return-exchanges-policy" className="hover:underline">
                                Returns & exchanges accepted
                            </a>
                        </Text>
                    </div>
                    {/* product variants */}
                    <div className="flex flex-col gap-2  ">
                        <ProductVariants
                            defaultConfig={product?.configurable_options}
                            onChangeConfig={onChangeConfig}
                            product={product as Product}
                            variant={variant}
                            key={product.id}
                        />
                        <CustomizationOptions
                            data={product?.productNote || []}
                            error={validCustomOptions}
                            onChange={handleOnChangeCustomization}
                            value={defaultProduct}
                        />
                        <div className="flex flex-col items-start gap-1 ">
                            <Text type="title-4" className="font-semibold">
                                Quantity
                            </Text>
                            <div className="flex gap-4 items-center px-1 lg:px-3 h-8 lg:h-10 border-[#D3D3D3] border-[1px] rounded-lg">
                                <Icon
                                    className="cursor-pointer"
                                    name="minus"
                                    onClick={() => {
                                        if (quantity > 1) {
                                            setQuantity(quantity - 1);
                                        }
                                    }}
                                />
                                <div
                                    contentEditable
                                    ref={quantityRef}
                                    onInput={handleInput}
                                    // onBlur={handleBlur}
                                    className="min-w-[0px] lg:min-w-[40px] text-center outline-transparent cursor-text"
                                    dangerouslySetInnerHTML={{ __html: quantity }}
                                />
                                <Icon
                                    className="cursor-pointer"
                                    name="add"
                                    onClick={() => {
                                        if (quantity < Number.MAX_SAFE_INTEGER) {
                                            setQuantity(quantity + 1);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    {/* action */}

                    <div
                        className={classNames(
                            'fixed bottom-0 left-0 p-4 lg:px-0 pt-4 bg-white lg:relative flex flex-col z-10 w-full'
                        )}
                    >
                        {renderActionButtons()}
                    </div>

                    {isClient && called && !!dataCutOff && isDisplay && (
                        <Text type="label-3" className="">
                            <div className="flex gap-2 font-semibold">
                                <TruckFastIcon className={'size-4'} /> Cut Off
                            </div>
                            {isBeforeChristmas ? (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: textBeforeChristmas?.replace(
                                            '{date}',
                                            toPrevDateOrderCutOffAfter.toString()
                                        )
                                    }}
                                ></div>
                            ) : (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: textAfterChristmas
                                    }}
                                ></div>
                            )}
                        </Text>
                    )}

                    <div>
                        <ProductDetailMoreInfo product={product as Product} variant={variant} />
                    </div>
                    <div className="block lg:hidden">
                        {windowWidth < 1024 && (
                            <TabReviews product_rating_summary={product.rating_summary} sku={product.sku} />
                        )}
                    </div>
                </div>
            </Container>
        </React.Fragment>
    );
};

export default ProductDetailTwo;

export const ProductPrice = ({
    finalPrice,
    regularPrice,
    discountPercent,
    loading
}: {
    finalPrice: number;
    regularPrice: number;
    discountPercent: number;
    loading?: boolean;
}) => {
    if (!finalPrice && !!loading) {
        return (
            <div className="flex justify-between w-full gap-1">
                <Skeleton containerClassName="flex-1" />
                <Skeleton containerClassName="flex-1 text-right" />
            </div>
        );
    }

    if (!finalPrice || !regularPrice) {
        return null;
    }

    return (
        <>
            <Text type="headline-3" weight={800} color={Colors.PRIMARY}>
                ${finalPrice?.toFixed(2)}
            </Text>
            {regularPrice - finalPrice > 0 && (
                <Text
                    className="line-through"
                    type="label-3"
                    style={{
                        color: Colors.GREY_400
                    }}
                >
                    ${regularPrice?.toFixed(2)}
                </Text>
            )}
        </>
    );
};
